import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTimezones } from '../../Common/hooks/useTimezones';
import { useEffect } from 'react';
import { pusherSubscribeObject } from '../../Common/utils/pusher';
import { graphql } from '../../__generated__';
import { Consult } from '../../__generated__/graphql';

export const customerRequestsQuery = graphql(`
  query getCustomerRequests($id: ID) {
    getCustomerRequests(id: $id) {
      __typename
      id
      state
      brand
      doctorEndedAt
      option
      isExpress
      documents {
        title
        key
        date
        share
      }
      steps {
        completed
        name
        type
        id
        notes
      }
      appointment {
        __typename
        id
        date
        confirmed
        cancelled
        userId
      }
      serviceTier {
        __typename
        name
        description
        id
        fields
      }
      requestedBy {
        __typename
        id
        name
        brand
        mobile
        email
        dob
        gender
        kyc
        allergies
        hearaboutus
        given
        surname
        address
        given
        suburb
        street
        state
        postcode
        medicalhistory
        medications
        medicarenumber
        medicareref
        medicareexp
        completedProfile
        prescriptionReady
        completedConsent
        alert {
          updatedAt
          alertMessage
        }
      }
      pricingTier {
        __typename
        id
        name
      }
      clinic {
        __typename
        name
        description
        id
        address
      }
      billedRefunded
      billedRefunds {
        id
        timestamp
        amount
        reason
      }
      requestedAt
      customerEndedAt
      customerFeedback
      customerRating
      state
      doctorNotes
      doctorTemplate
      videoCall
      videoCallAnswered
      videoCallAnsweredAt
      doctorAssigned {
        __typename
        id
        name
        given
        doctorBio
        doctorImage
      }
      doctorAssignedAt
      subscribe
      subscribeActive
      subscribeNext
      billed
      billedError
      billedAmount
      billedDoctorAmount
      billedPlatformFee
      timedOut
      treatmentPlan
      treatmentPlanAccepted
      additionalForms {
        __typename
        form {
          id
          title
        }
        values {
          field {
            order
            name
            type
            markdown
          }
          notes
          value
        }
      }
      form {
        __typename
        form {
          id
        }
        values {
          field {
            order
            name
            type
          }
          notes
          value
        }
      }
    }
  }
`);

export const useDoctorCustomerRequests = (customerID: string) => {
  const { convertUTCDateToUserISO } = useTimezones();

  const { loading, data, error, refetch } = useQuery(customerRequestsQuery, {
    variables: {
      id: customerID,
    },
  });

  useEffect(() => {
    const { unsubscribe } = pusherSubscribeObject(customerID ?? '', 'requests', () => refetch());

    return () => {
      unsubscribe();
    };
  }, [customerID]);

  const requests = [...(data?.getCustomerRequests ?? [])].sort((a, b) =>
    dayjs(a.requestedAt).isAfter(dayjs(b.requestedAt)) ? 0 : -1,
  ) as Consult[];

  const requestsDescOrder = [...(data?.getCustomerRequests ?? [])].sort((a, b) =>
    dayjs(b.requestedAt).isAfter(dayjs(a.requestedAt)) ? 0 : -1,
  ) as Consult[];

  const thisWeek = data?.getCustomerRequests?.filter((request) =>
    convertUTCDateToUserISO(request.requestedAt).isAfter(dayjs().subtract(7, 'days')),
  ) as Consult[];

  return {
    loading,
    requests,
    requestsDescOrder,
    error,
    thisWeek,
  };
};
